import '@fe-toolkit/web-components/fonts.css'
import '@fe-toolkit/web-components/base-styles.css'
// Declares Inter Tight, which fe-common's brand-refresh CommonHeader uses. The design
// system does not import it itself, so each app must (fe-common does the same in
// its src/modern.runtime.ts).
import '@giffgaff/fe-design-system-fonts/fonts.css'

import { AppContext, AppProps } from 'next/app'
import getConfig from 'next/config'
import { useRouter } from 'next/router'

import styled from 'styled-components'

import { Suspense, useEffect, useMemo, useState } from 'react'

import { setDebugMode } from '@fe-toolkit/eventing'
import { setupObservability } from '@fe-toolkit/observability/client'

import { useAnalyticsSetup, useDynamicYieldSetup } from '@fe-toolkit/analytics/react'

import { getClientCookieConsent } from '@fe-toolkit/utility/client'

import { getObservabilityUrl } from '@fe-toolkit/observability'

import { getServerSideFeaturesNJS } from '@fe-toolkit/utility/client/tracking-context'
import { headerFlagKeys } from '@giffgaff/fe-design-system-content'

import { isDev, isMockServiceEnabled, isServer } from '@/constants'
import Providers from '@/context/Providers'
import logger from '@/lib/logger'
import getChromeFlags, { ChromeFlags } from '@/api/fe-common/getChromeFlags'
import getOwnChromeFlags from '@/api/client-tracking-context/getOwnChromeFlags'
import ChromeFlagsContext, { OFF_CHROME_FLAGS } from '@/layouts/Main/ChromeFlagsContext'
import { BrandRefreshContext, resolveBrandRefresh } from '@/utils/brandRefresh/brandRefresh'

const { publicRuntimeConfig } = getConfig()

// Singleton pattern for server-side MSW initialization
let serverMswInitialized = false
let serverMswPromise: Promise<void> | null = null

const initServerMsw = async (): Promise<void> => {
  if (serverMswInitialized) {
    return
  }

  if (serverMswPromise) {
    return serverMswPromise
  }

  serverMswPromise = (async () => {
    try {
      const { server } = await import('@/mocks/server')

      server.listen()
      serverMswInitialized = true
      console.info('MSW: Server listening')
    } catch (error) {
      console.error('MSW: Failed to initialize server', error)
      throw error
    }
  })()

  return serverMswPromise
}

if (typeof window !== 'undefined') {
  setDebugMode(isDev)

  // Without a base URL (e.g. local dev, where OBSERVABILITY_BASE_URL isn't set), getObservabilityUrl
  // falls back to the production collector — skip setup entirely rather than risk pointing at it.
  if (publicRuntimeConfig.OBSERVABILITY_BASE_URL) {
    setupObservability({
      mfeName: 'broadband-profile-fe',
      version: publicRuntimeConfig.VERSION,
      tracesUrl: getObservabilityUrl({
        type: 'traces',
        observabilityBaseUrl: publicRuntimeConfig.OBSERVABILITY_BASE_URL,
      }),
      metricsUrl: getObservabilityUrl({
        type: 'metrics',
        observabilityBaseUrl: publicRuntimeConfig.OBSERVABILITY_BASE_URL,
      }),
      logsUrl: getObservabilityUrl({
        type: 'logs',
        observabilityBaseUrl: publicRuntimeConfig.OBSERVABILITY_BASE_URL,
      }),
      enableLogs: true,
      enableTracing: false,
      enableMetrics: true,
    })
  }
}

// This is to avoid FOUC (Flash of Unstyled Content)
const Wrapper = styled.div`
  display: block !important;
`

function MyApp({
  Component,
  pageProps,
  chromeFlags,
  isBrandRefresh,
}: AppProps & { chromeFlags?: ChromeFlags; isBrandRefresh?: boolean }) {
  // Kept from the server render: getInitialProps runs again in the browser on every client-side
  // navigation, where there is no request to look the flags up for.
  const [serverChromeFlags] = useState(chromeFlags)
  const [brandRefresh] = useState(Boolean(isBrandRefresh))
  const router = useRouter()
  const [mswReady, setMswReady] = useState(!isMockServiceEnabled() || isServer)
  const cookieConsent = useMemo(() => (isServer ? false : getClientCookieConsent()), [])

  useAnalyticsSetup({
    enabled: publicRuntimeConfig.ENABLE_SNOWPLOW === 'true',
    collectorUrl: publicRuntimeConfig.SNOWPLOW_COLLECTOR_URL,
    appId: 'broadband-profile-fe',
    anonymousTracking: { withServerAnonymisation: true },
    cookieSecure: true,
    pathname: router.asPath,
  })

  // only pass in the DY_SITE_ID if it's correct, i.e., numeric only
  useDynamicYieldSetup({
    siteId: /^\d+$/.test(publicRuntimeConfig.DY_SITE_ID) ? publicRuntimeConfig.DY_SITE_ID : undefined,
    cookieConsent,
    contextType: 'OTHER',
    pathname: router.asPath,
  })

  useEffect(() => {
    logger.info('broadband-profile-fe client initialised')
  }, [])

  useEffect(() => {
    // Initialize MSW on client-side before rendering
    if (isMockServiceEnabled() && isDev && !isServer) {
      import('@/mocks/browser')
        .then(({ initMsw }) => initMsw())
        .then(() => {
          console.info('MSW: Ready')
          setMswReady(true)
        })
        .catch(error => {
          console.error('MSW: Failed to initialize', error)
          setMswReady(true) // Continue anyway
        })
    }
  }, [])

  if (!mswReady) {
    return null // Don't render until MSW is ready
  }

  return (
    <BrandRefreshContext.Provider value={brandRefresh}>
      <Wrapper style={{ display: 'none' }}>
        <Suspense>
          {/* Above Providers, not around Component as in the template, because Providers renders MainLayout. */}
          <ChromeFlagsContext.Provider value={serverChromeFlags ?? OFF_CHROME_FLAGS}>
            <Providers>
              <Component {...pageProps} />
            </Providers>
          </ChromeFlagsContext.Provider>
        </Suspense>
      </Wrapper>
    </BrandRefreshContext.Provider>
  )
}

MyApp.getInitialProps = async ({ Component, ctx }: AppContext) => {
  let pageProps = {}
  // Started first so it runs alongside the CTC call below; it never rejects.
  const feCommonChromeFlags = getChromeFlags(ctx)
  let ownChromeFlags = OFF_CHROME_FLAGS

  // fe-common's decision when it arrived, else this MFE's own, as in fe-modern-template. Either way all three
  // values are passed, so fe-common's components never fall back on their own lookup.
  const chromeProps = async () => {
    const chromeFlags = (await feCommonChromeFlags) ?? ownChromeFlags

    // These props put this visitor's CTC assignment into __NEXT_DATA__, and server.mjs marks pages public,
    // so stop shared caches serving one visitor's header and footer to others.
    if (ctx.res && !ctx.res.headersSent) {
      ctx.res.setHeader('Cache-Control', 'private, no-cache, no-store, must-revalidate')
    }

    return { chromeFlags, isBrandRefresh: resolveBrandRefresh(ctx.query, chromeFlags) }
  }

  try {
    // Initialize server-side MSW before any requests
    if (isMockServiceEnabled() && isDev && isServer) {
      try {
        await initServerMsw()
      } catch (error) {
        console.error('MSW: Server initialization failed, continuing without mocks', error)
      }
    }

    // getServerSideFeaturesNJS replaces Set-Cookie wholesale, dropping cookies already queued for this
    // response (middleware's _dyid_server), so they are put back afterwards.
    const queuedCookies = ctx.res?.getHeader('Set-Cookie')

    // Maintenance mode check. Also asks for the header and footer flags, which getOwnChromeFlags reads,
    // so this stays the only CTC call per request.
    const features = await getServerSideFeaturesNJS({
      requestedFeatures: ['broadband-down-for-maintenance', ...Object.values(headerFlagKeys)],
      apiGatewayUrl: process.env.PUBLIC_API_URL!,
      nextPageContext: ctx,
    })

    if (queuedCookies) ctx.res?.appendHeader('Set-Cookie', queuedCookies as string | string[])

    ownChromeFlags = getOwnChromeFlags(features)
    const maintenanceFeature = features.find(f => f.name === 'broadband-down-for-maintenance')

    if (maintenanceFeature?.value === 'true') {
      const destination = 'https://www.giffgaff.com/broadband/site-maintenance'

      if (ctx.res) {
        // server.mjs sets a 1hr Cache-Control on every response before the Next handler runs
        // override it here so this redirect is never cached.
        ctx.res.writeHead(302, { Location: destination, 'Cache-Control': 'no-store' })
        ctx.res.end()
      } else if (typeof window !== 'undefined') {
        window.location.replace(destination)
      }

      return {}
    }

    // Retrieve page-specific props
    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps(ctx)
    }

    return { ...pageProps, ...(await chromeProps()) }
  } catch (error) {
    console.error('Error in _app.tsx:', error)

    return { ...pageProps, ...(await chromeProps()) }
  }
}

export default MyApp
